import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Calendar, Users, Settings, FileText, Image, 
  MessageSquare, Zap, LogOut, Menu, X, Plus, Trash2, Edit2, 
  CheckCircle, ChevronDown, UserPlus, Shield, HardHat,
  Briefcase, ClipboardList, BarChart3, PenSquare, Activity, Mail, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import StaffCalendarTab from './StaffCalendarTab';
import JobsTab from './JobsTab';
import FormsTab from './FormsTab';
import MetricsTab from './MetricsTab';
import BlogTab from './BlogTab';
import SEOTab from './SEOTab';
import ReachoutTab from './ReachoutTab';
import ServiceSEOEditor from './ServiceSEOEditor';
import LeadsTab from './LeadsTab';
import OverviewTab from './OverviewTab';
import BookingsTab from './BookingsTab';
import CustomersTab from './CustomersTab';

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/co7mv4rs_IslandCoastalElectric-MountainOnly-Logo.V3-VECTOR.png";

const AdminDashboard = () => {
  // Sidebar closed by default on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [activeTab, setActiveTab] = useState('overview');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle URL params (e.g., ?tab=blog&approve=token)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) setActiveTab(tab);
  }, [location.search]);
  
  // Close sidebar on mobile when clicking a nav item
  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Data states
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [
        bookingsRes, customersRes, servicesRes, testimonialsRes,
        faqsRes, galleryRes, messagesRes, usersRes, staffRes, landingRes, settingsRes
      ] = await Promise.all([
        api.admin.getBookings(),
        api.admin.getCustomers(),
        api.admin.getServices(),
        api.admin.getTestimonials(),
        api.admin.getFaqs(),
        api.admin.getGallery(),
        api.admin.getContactMessages(),
        api.admin.getUsers(),
        api.admin.getStaff(),
        api.admin.getLandingPages(),
        api.admin.getSettings()
      ]);
      
      setBookings(bookingsRes.data);
      setCustomers(customersRes.data);
      setServices(servicesRes.data);
      setTestimonials(testimonialsRes.data);
      setFaqs(faqsRes.data);
      setGallery(galleryRes.data);
      setMessages(messagesRes.data);
      setUsers(usersRes.data);
      setStaff(staffRes.data);
      setLandingPages(landingRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const navItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
    { id: 'leads', icon: UserCheck, label: 'Leads' },
    { id: 'staff', icon: HardHat, label: 'Staff & Calendar' },
    { id: 'jobs', icon: Briefcase, label: 'Jobs' },
    { id: 'forms', icon: ClipboardList, label: 'Forms' },
    { id: 'blog', icon: PenSquare, label: 'Blog' },
    { id: 'seo', icon: Activity, label: 'SEO Intelligence' },
    { id: 'reachout', icon: Mail, label: 'Customer Reachout' },
    { id: 'metrics', icon: BarChart3, label: 'Metrics' },
    { id: 'bookings', icon: Calendar, label: 'Bookings' },
    { id: 'customers', icon: Users, label: 'Customers' },
    { id: 'services', icon: Zap, label: 'Services' },
    { id: 'realresults', icon: Image, label: 'Real Results' },
    { id: 'faqs', icon: FileText, label: 'FAQs' },
    { id: 'messages', icon: MessageSquare, label: 'Messages' },
    { id: 'landing', icon: FileText, label: 'Landing Pages' },
    { id: 'users', icon: Shield, label: 'Users' },
    { id: 'settings', icon: Settings, label: 'Settings' }
  ];

  return (
    <div className="min-h-screen bg-primary-dark flex" data-testid="admin-dashboard">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`admin-sidebar fixed lg:relative z-40 h-screen transition-all duration-300 ${
        sidebarOpen ? 'w-56 translate-x-0' : 'w-0 -translate-x-full lg:translate-x-0 lg:w-14'
      } overflow-hidden`}>
        <div className="h-full flex flex-col w-56 lg:w-auto">
          {/* Logo */}
          <div className="p-3 flex items-center justify-between border-b border-white/10">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_ICON} alt="" className="h-7 w-auto" />
              {sidebarOpen && (
                <span className="text-white font-bold text-sm">Admin</span>
              )}
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`admin-nav-item w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === item.id ? 'active' : 'text-white/60'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </button>
            ))}
          </nav>

          {/* User Info */}
          <div className="p-3 border-t border-white/10">
            {sidebarOpen && (
              <div className="mb-2">
                <p className="text-white font-medium text-sm truncate">{user?.name}</p>
                <p className="text-white/50 text-xs truncate">{user?.email}</p>
              </div>
            )}
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="w-full border-white/20 text-white/60 hover:text-white hover:border-white/40"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {sidebarOpen && <span className="ml-2">Logout</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-auto">
        {/* Top Bar */}
        <header className="sticky top-0 z-20 bg-secondary-dark border-b border-white/10 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-white p-1"
                data-testid="toggle-sidebar"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-bold text-white capitalize truncate">{activeTab}</h1>
            </div>
            <Link to="/" className="text-electric-teal text-sm font-medium hover:underline whitespace-nowrap" data-testid="view-site-btn">
              View Site →
            </Link>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-4 md:p-6">
          {activeTab === 'overview' && <OverviewTab bookings={bookings} customers={customers} messages={messages} />}
          {activeTab === 'leads' && <LeadsTab />}
          {activeTab === 'staff' && <StaffCalendarTab staff={staff} onUpdate={loadData} />}
          {activeTab === 'jobs' && <JobsTab staff={staff} onUpdate={loadData} />}
          {activeTab === 'forms' && <FormsTab customers={customers} />}
          {activeTab === 'blog' && <BlogTab />}
          {activeTab === 'seo' && <SEOTab />}
          {activeTab === 'reachout' && <ReachoutTab />}
          {activeTab === 'metrics' && <MetricsTab />}
          {activeTab === 'bookings' && <BookingsTab bookings={bookings} staff={staff} onUpdate={loadData} />}
          {activeTab === 'customers' && <CustomersTab customers={customers} onUpdate={loadData} />}
          {activeTab === 'services' && <ServicesTab services={services} onUpdate={loadData} />}
          {activeTab === 'realresults' && <RealResultsTab testimonials={testimonials} gallery={gallery} onUpdate={loadData} />}
          {activeTab === 'faqs' && <FAQsTab faqs={faqs} onUpdate={loadData} />}
          {activeTab === 'messages' && <MessagesTab messages={messages} onUpdate={loadData} />}
          {activeTab === 'landing' && <LandingPagesTab pages={landingPages} onUpdate={loadData} />}
          {activeTab === 'users' && <UsersTab users={users} onUpdate={loadData} />}
          {activeTab === 'settings' && <SettingsTab settings={settings} onUpdate={loadData} />}
        </div>
      </main>
    </div>
  );
};

// Generic CRUD Tab Component
const CRUDTab = ({ items, fields, onAdd, onUpdate, onDelete, title }) => {
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleSubmit = async () => {
    try {
      if (editItem) {
        await onUpdate(editItem.id, formData);
        toast.success(`${title} updated`);
      } else {
        await onAdd(formData);
        toast.success(`${title} created`);
      }
      setIsDialogOpen(false);
      setEditItem(null);
      setFormData({});
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormData(item);
    setIsDialogOpen(true);
  };

  const openNew = () => {
    setEditItem(null);
    setFormData(fields.reduce((acc, f) => ({ ...acc, [f.name]: f.default || '' }), {}));
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openNew} className="bg-electric-teal text-primary-dark font-bold">
          <Plus className="w-4 h-4 mr-2" />
          Add {title}
        </Button>
      </div>

      <div className="grid gap-4">
        {items.map((item) => (
          <div key={item.id} className="bg-secondary-dark p-4 rounded-lg border border-white/10 flex justify-between items-start">
            <div className="flex-1">
              <h4 className="text-white font-medium">{item[fields[0].name]}</h4>
              {fields[1] && <p className="text-white/60 text-sm mt-1">{item[fields[1].name]}</p>}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => openEdit(item)} variant="ghost" size="sm" className="text-white/60 hover:text-white">
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button onClick={() => onDelete(item.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-400">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-secondary-dark border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{editItem ? `Edit ${title}` : `Add ${title}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {fields.map((field) => (
              <div key={field.name}>
                <Label className="text-white mb-2 block">{field.label}</Label>
                {field.type === 'textarea' ? (
                  <Textarea
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="bg-primary-dark border-white/20 text-white"
                  />
                ) : (
                  <Input
                    type={field.type || 'text'}
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="bg-primary-dark border-white/20 text-white"
                  />
                )}
              </div>
            ))}
            <Button onClick={handleSubmit} className="w-full bg-electric-teal text-primary-dark font-bold">
              {editItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Services Tab
const ServicesTab = ({ services, onUpdate }) => {
  const [editingService, setEditingService] = useState(null);

  const handleAdd = async (data) => {
    await api.admin.createService(data);
    onUpdate();
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await api.admin.deleteService(id);
    toast.success('Service deleted');
    onUpdate();
  };

  if (editingService) {
    return (
      <div data-testid="services-tab">
        <ServiceSEOEditor
          service={editingService}
          onBack={() => { setEditingService(null); onUpdate(); }}
          onSaved={() => { setEditingService(null); onUpdate(); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="services-tab">
      <div className="flex justify-end">
        <Button
          onClick={() => {
            const newService = { title: '', slug: '', short_description: '', full_description: '', image_url: '', features: [] };
            handleAdd(newService).then(() => onUpdate());
          }}
          className="bg-electric-teal text-primary-dark font-bold"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Service
        </Button>
      </div>

      <div className="grid gap-3">
        {services.map((service) => (
          <div key={service.id} className="bg-secondary-dark rounded-lg border border-white/10 p-4 flex items-center justify-between group hover:border-white/20 transition-colors">
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setEditingService(service)} data-testid={`edit-service-${service.slug}`}>
              <h4 className="text-white font-medium group-hover:text-electric-teal transition-colors">{service.title}</h4>
              <p className="text-white/40 text-sm mt-0.5 truncate">/{service.slug}</p>
              <div className="flex items-center gap-3 mt-2 text-white/30 text-xs">
                <span>{service.features?.length || 0} features</span>
                <span>{service.process_steps?.length || 0} steps</span>
                <span>{service.pricing_factors?.length || 0} pricing</span>
                <span>{service.faqs?.length || 0} FAQs</span>
                {service.meta_title && <span className="text-green-400/60">SEO</span>}
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <Button
                onClick={() => setEditingService(service)}
                variant="ghost"
                size="sm"
                className="text-electric-teal hover:text-electric-teal/80"
                data-testid={`edit-btn-${service.slug}`}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => handleDelete(service.id)}
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Real Results Tab (Combined Testimonials + Gallery)
const RealResultsTab = ({ testimonials, gallery, onUpdate }) => {
  const [activeSubTab, setActiveSubTab] = useState('testimonials');
  
  // Testimonial handlers
  const handleAddTestimonial = async (data) => {
    await api.admin.createTestimonial({ ...data, rating: parseInt(data.rating) || 5 });
    onUpdate();
  };
  const handleEditTestimonial = async (id, data) => {
    await api.admin.updateTestimonial(id, { ...data, rating: parseInt(data.rating) || 5 });
    onUpdate();
  };
  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    await api.admin.deleteTestimonial(id);
    toast.success('Testimonial deleted');
    onUpdate();
  };

  // Gallery handlers
  const handleAddGallery = async (data) => {
    await api.admin.createGalleryItem(data);
    onUpdate();
  };
  const handleEditGallery = async (id, data) => {
    await api.admin.updateGalleryItem(id, data);
    onUpdate();
  };
  const handleDeleteGallery = async (id) => {
    if (!window.confirm('Delete this gallery item?')) return;
    await api.admin.deleteGalleryItem(id);
    toast.success('Gallery item deleted');
    onUpdate();
  };

  return (
    <div data-testid="realresults-tab">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList className="bg-primary-dark border border-white/10 mb-6">
          <TabsTrigger value="testimonials" className="data-[state=active]:bg-electric-teal data-[state=active]:text-primary-dark">
            Testimonials
          </TabsTrigger>
          <TabsTrigger value="gallery" className="data-[state=active]:bg-electric-teal data-[state=active]:text-primary-dark">
            Project Gallery
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="testimonials">
          <CRUDTab
            items={testimonials}
            fields={[
              { name: 'customer_name', label: 'Customer Name' },
              { name: 'customer_location', label: 'Location' },
              { name: 'content', label: 'Testimonial Content', type: 'textarea' },
              { name: 'rating', label: 'Rating (1-5)', default: '5' },
              { name: 'image_url', label: 'Photo URL (Optional - photo of the work done)' }
            ]}
            onAdd={handleAddTestimonial}
            onUpdate={handleEditTestimonial}
            onDelete={handleDeleteTestimonial}
            title="Testimonial"
          />
        </TabsContent>
        
        <TabsContent value="gallery">
          <CRUDTab
            items={gallery}
            fields={[
              { name: 'title', label: 'Title' },
              { name: 'description', label: 'Description', type: 'textarea' },
              { name: 'category', label: 'Category' },
              { name: 'image_url', label: 'Image URL' }
            ]}
            onAdd={handleAddGallery}
            onUpdate={handleEditGallery}
            onDelete={handleDeleteGallery}
            title="Gallery Item"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// FAQs Tab
const FAQsTab = ({ faqs, onUpdate }) => {
  const handleAdd = async (data) => {
    await api.admin.createFaq({ ...data, order: parseInt(data.order) || 0 });
    onUpdate();
  };
  const handleEdit = async (id, data) => {
    await api.admin.updateFaq(id, { ...data, order: parseInt(data.order) || 0 });
    onUpdate();
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    await api.admin.deleteFaq(id);
    toast.success('FAQ deleted');
    onUpdate();
  };

  return (
    <div data-testid="faqs-tab">
      <CRUDTab
        items={faqs}
        fields={[
          { name: 'question', label: 'Question' },
          { name: 'answer', label: 'Answer', type: 'textarea' },
          { name: 'order', label: 'Order', default: '0' }
        ]}
        onAdd={handleAdd}
        onUpdate={handleEdit}
        onDelete={handleDelete}
        title="FAQ"
      />
    </div>
  );
};

// Messages Tab
const MessagesTab = ({ messages, onUpdate }) => {
  const handleMarkRead = async (id) => {
    await api.admin.markMessageRead(id);
    onUpdate();
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    await api.admin.deleteMessage(id);
    toast.success('Message deleted');
    onUpdate();
  };

  return (
    <div className="space-y-4" data-testid="messages-tab">
      {messages.map((message) => (
        <div key={message.id} className={`bg-secondary-dark p-6 rounded-lg border ${message.is_read ? 'border-white/10' : 'border-electric-teal/50'}`}>
          <div className="flex justify-between items-start mb-4">
            <div>
              <h4 className="text-white font-medium">{message.name}</h4>
              <p className="text-white/60 text-sm">{message.email} • {message.phone}</p>
            </div>
            <div className="flex gap-2">
              {!message.is_read && (
                <Button onClick={() => handleMarkRead(message.id)} variant="ghost" size="sm" className="text-electric-teal">
                  <CheckCircle className="w-4 h-4" />
                </Button>
              )}
              <Button onClick={() => handleDelete(message.id)} variant="ghost" size="sm" className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-white/80">{message.message}</p>
        </div>
      ))}
    </div>
  );
};

// Landing Pages Tab
const LandingPagesTab = ({ pages, onUpdate }) => {
  const handleAdd = async (data) => {
    await api.admin.createLandingPage({ ...data, benefits: data.benefits?.split(',').map(b => b.trim()) || [] });
    onUpdate();
  };
  const handleEdit = async (id, data) => {
    await api.admin.updateLandingPage(id, { ...data, benefits: data.benefits?.split(',').map(b => b.trim()) || [] });
    onUpdate();
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this landing page?')) return;
    await api.admin.deleteLandingPage(id);
    toast.success('Landing page deleted');
    onUpdate();
  };

  const pagesWithBenefitsString = pages.map(p => ({ ...p, benefits: p.benefits?.join(', ') || '' }));

  return (
    <div data-testid="landing-pages-tab">
      <CRUDTab
        items={pagesWithBenefitsString}
        fields={[
          { name: 'title', label: 'Title' },
          { name: 'slug', label: 'Slug' },
          { name: 'headline', label: 'Headline' },
          { name: 'subheadline', label: 'Subheadline' },
          { name: 'cta_text', label: 'CTA Text' },
          { name: 'cta_link', label: 'CTA Link' },
          { name: 'service_focus', label: 'Service Focus' },
          { name: 'benefits', label: 'Benefits (comma separated)' },
          { name: 'image_url', label: 'Image URL' }
        ]}
        onAdd={handleAdd}
        onUpdate={handleEdit}
        onDelete={handleDelete}
        title="Landing Page"
      />
    </div>
  );
};

// Users Tab
const UsersTab = ({ users, onUpdate }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'staff' });

  const handleAdd = async () => {
    try {
      await api.admin.createUser(formData);
      toast.success('User created');
      setIsDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'staff' });
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.admin.deleteUser(id);
      toast.success('User deleted');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex justify-end">
        <Button onClick={() => setIsDialogOpen(true)} className="bg-electric-teal text-primary-dark font-bold">
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      <div className="bg-secondary-dark rounded-lg border border-white/10 overflow-hidden">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="text-left text-white/60 text-sm font-medium p-4">Name</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Email</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Role</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td className="p-4 text-white font-medium">{user.name}</td>
                <td className="p-4 text-white/80">{user.email}</td>
                <td className="p-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                    user.role === 'admin' ? 'bg-electric-teal/20 text-electric-teal' : 'bg-white/10 text-white/60'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="p-4">
                  <Button onClick={() => handleDelete(user.id)} variant="ghost" size="sm" className="text-red-500 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-secondary-dark border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white mb-2 block">Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-primary-dark border-white/20 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-primary-dark border-white/20 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-primary-dark border-white/20 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAdd} className="w-full bg-electric-teal text-primary-dark font-bold">
              Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Settings Tab
const SettingsTab = ({ settings, onUpdate }) => {
  const [formData, setFormData] = useState(settings);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.admin.updateSettings(formData);
      toast.success('Settings saved');
      onUpdate();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-tab">
      <div className="bg-secondary-dark p-6 rounded-lg border border-white/10">
        <h3 className="text-white font-bold mb-6">Site Settings</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-white mb-2 block">Company Name</Label>
            <Input
              value={formData.company_name || ''}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="bg-primary-dark border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Tagline</Label>
            <Input
              value={formData.tagline || ''}
              onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
              className="bg-primary-dark border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Phone</Label>
            <Input
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="bg-primary-dark border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Email</Label>
            <Input
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-primary-dark border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Address</Label>
            <Input
              value={formData.address || ''}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="bg-primary-dark border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Business Hours</Label>
            <Input
              value={formData.business_hours || ''}
              onChange={(e) => setFormData({ ...formData, business_hours: e.target.value })}
              className="bg-primary-dark border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Hero Title</Label>
            <Input
              value={formData.hero_title || ''}
              onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
              className="bg-primary-dark border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Hero Subtitle</Label>
            <Input
              value={formData.hero_subtitle || ''}
              onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
              className="bg-primary-dark border-white/20 text-white"
            />
          </div>
          <Button onClick={handleSave} disabled={loading} className="w-full bg-electric-teal text-primary-dark font-bold">
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
